import moment from "moment";
import { Range, RangeWithKey } from "react-date-range";
import { MoneyApi } from "go-core/api/types";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import {
	FILTER_SEPARATOR,
	FILTER_VALUE_SEPARATOR,
	NEW_WAY_TO_ENCODING_FILTER_SIGN,
} from "go-list/core/components/Filter/services";
import {
	isPredefinedRange,
	parsePredefinedRangeToDefaultString,
	parsePredefinedRangeToRange,
	parseRangeToDefaultString,
} from "go-list/utils/daterangeutils";
import { SubReportApi } from "go-report/core/services/types";
import { ChartDataRangeType, DashboardReportFilter } from "../services/types";

const mockedDateRange = "2022-07-01 00:00:00 - 2022-07-31 23:59:59";

export const parsePredefinedRangeToPreviousPeriodRange = (
	predefinedRange: string,
	dateToString?: boolean
): string | Range => {
	const newPredefinedRange = predefinedRange.replace(",", FILTER_VALUE_SEPARATOR);
	let currentDateRange: RangeWithKey | Range = {};

	if (isPredefinedRange(newPredefinedRange)) {
		const predefinedRangeObj = parsePredefinedRangeToRange(newPredefinedRange);
		if (predefinedRangeObj) currentDateRange = predefinedRangeObj;
	} else {
		const [startDateString, endDateString] = newPredefinedRange.split(FILTER_VALUE_SEPARATOR);
		const startDate = moment(startDateString).toDate();
		const endDate = moment(endDateString).toDate();

		currentDateRange = { startDate, endDate };
	}

	const { startDate, endDate } = currentDateRange;

	if (!startDate || !endDate) return "Invalid date range";

	const rangeDifferenceInMilliseconds = endDate.getTime() - startDate.getTime();
	const previousPeriodStartDate = moment(startDate)
		.subtract(rangeDifferenceInMilliseconds, "ms")
		.startOf("day")
		.toDate();
	const previousPeriodDateRange = { startDate: previousPeriodStartDate, endDate: startDate };

	return dateToString ? parseRangeToDefaultString(previousPeriodDateRange) : previousPeriodDateRange;
};

const processFilter = (config: DashboardReportFilter): string => {
	let { dateRange: dateFromFilter, organizationIds } = config;

	dateFromFilter = dateFromFilter.replace(",", FILTER_VALUE_SEPARATOR);
	const date = isPredefinedRange(dateFromFilter)
		? parsePredefinedRangeToDefaultString(dateFromFilter)
		: dateFromFilter;
	const previousDateRange = parsePredefinedRangeToPreviousPeriodRange(dateFromFilter, true);

	let filter = `${NEW_WAY_TO_ENCODING_FILTER_SIGN}date_range|bt=${date}${FILTER_SEPARATOR}compare_date_range|bt=${previousDateRange}${FILTER_SEPARATOR}enable=true${FILTER_SEPARATOR}groups|e=${config.defaultGroup}${FILTER_VALUE_SEPARATOR}${config.chartType}${FILTER_SEPARATOR}time_start=00:00${FILTER_SEPARATOR}time_end=23:59`;
	if (config.type) {
		filter += `${FILTER_SEPARATOR}type=${config.type}`;
	}
	if (config.include) {
		filter += `${FILTER_SEPARATOR}include|e=${config.include}`;
	}
	if (organizationIds) {
		organizationIds = organizationIds.replaceAll(",", FILTER_VALUE_SEPARATOR);
		filter += `${FILTER_SEPARATOR}organization_id|e=${organizationIds}`;
	} else {
		filter += `${FILTER_SEPARATOR}organization_id|e=${config.organizationId}`;
	}
	return btoa(unescape(encodeURIComponent(filter)));
};

const processDashboardReportRequestParams = (config: DashboardReportFilter): Record<string, any> => {
	const newParams: Record<string, any> = { f: processFilter(config) };

	if (config.size) newParams.size = config.size;

	return newParams;
};

const renderAggregateValue = (aggregate: number | MoneyApi, currency: string): number | string => {
	if (typeof aggregate === "number") return aggregate;

	if (!aggregate) {
		return FormatMoney({ amount: 0, currency });
	}

	return FormatMoney({
		amount: aggregate.amount || 0,
		currency,
	});
};

const getDashboardChartType = (report?: SubReportApi): ChartDataRangeType => {
	return report?.sub_report[0]?.group_by_type as ChartDataRangeType;
};

const checkIsEmptyData = (data: Record<string, any>): boolean => {
	if (!data?.payment_reports?.reports?.sub_report || data.payment_reports.reports.sub_report.length === 0)
		return true;

	return false;
};

export {
	processDashboardReportRequestParams,
	renderAggregateValue,
	getDashboardChartType,
	mockedDateRange,
	checkIsEmptyData,
};
